import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Copy, Send, X, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { safeStorage } from "@/lib/storage";

/**
 * ReferralPrompt — auto-triggered modal that appears the FIRST time a case's
 * payment succeeds. Tells the user "You just paid — invite a family member
 * and you both get $50 off."
 *
 * Trigger: PaymentSuccessPage dispatches
 *   window.dispatchEvent(new CustomEvent("gcu:payment-paid", { detail: { caseId } }))
 * once the Stripe session transitions to paid. This is exactly when the
 * referral credit is actually earned server-side, so the copy stays honest.
 * Shows once per caseId (persisted in localStorage).
 */
const SHOWN_KEY_PREFIX = "referral-prompt-shown:";

function alreadyShown(caseId) {
  if (!caseId) return false;
  return !!safeStorage.get(`${SHOWN_KEY_PREFIX}${caseId}`);
}

function markShown(caseId) {
  if (!caseId) return;
  safeStorage.set(`${SHOWN_KEY_PREFIX}${caseId}`, String(Date.now()));
}

export default function ReferralPrompt() {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onPaid = async (e) => {
      const caseId = e.detail?.caseId;
      if (alreadyShown(caseId)) return;
      try {
        const { data } = await api.get("/referrals/summary");
        setSummary(data);
        setOpen(true);
        markShown(caseId);
      } catch (err) {
        console.warn("[ReferralPrompt] summary load failed:", err?.message || err);
      }
    };
    window.addEventListener("gcu:payment-paid", onPaid);
    return () => window.removeEventListener("gcu:payment-paid", onPaid);
  }, []);

  const shareText = summary
    ? `I'm using DS-260USA.com for our family's DS-260 consular cases — it prepares every family member's DS-260 in your own language. Use my link and we both get $50 off: ${summary.share_url}`
    : "";

  const copy = async () => {
    if (!summary) return;
    try {
      await navigator.clipboard.writeText(summary.share_url);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy — please long-press the link.");
    }
  };

  const share = async () => {
    if (!summary) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "DS-260USA.com — $50 off",
          text: shareText,
          url: summary.share_url,
        });
      } catch (err) {
        if (err?.name !== "AbortError") console.warn(err);
      }
    } else {
      copy();
    }
  };

  if (!open || !summary) return null;

  const perRef = Math.round((summary.credit_per_referral_cents || 5000) / 100);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      style={{ background: "rgba(26,46,36,0.65)" }}
      onClick={() => setOpen(false)}
      data-testid="referral-prompt-modal"
    >
      <div
        className="card-surface p-6 sm:p-8 w-full max-w-md anim-in relative"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "linear-gradient(180deg, #FFFFFF 0%, var(--ai-bg) 100%)" }}
      >
        <button
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 p-1.5 rounded hover:bg-black/5"
          aria-label="Close"
          data-testid="referral-prompt-close-btn"
        >
          <X className="w-4 h-4" style={{ color: "var(--text-secondary)" }} />
        </button>

        <div
          className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
          style={{ background: "var(--action)" }}
        >
          <Gift className="w-7 h-7 text-white" />
        </div>

        <div className="pill pill-ok mb-3 text-xs">
          <Sparkles className="w-3 h-3" /> Payment confirmed
        </div>
        <h2 className="font-display text-2xl font-extrabold leading-tight mb-2" style={{ color: "var(--text-primary)" }}>
          You're all set — nice work!
        </h2>
        <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>
          Know someone else who's petitioning? Send them your link and{" "}
          <b style={{ color: "var(--action)" }}>you both get ${perRef} off</b> your next payment
          once they make their first payment.
        </p>

        <div className="flex gap-2 mb-4">
          <input
            readOnly
            value={summary.share_url}
            onFocus={(e) => e.target.select()}
            className="field-input text-xs flex-1 font-mono"
            style={{ background: "#fff" }}
            data-testid="referral-prompt-share-url"
            aria-label="Your referral link"
          />
          <button
            type="button"
            onClick={copy}
            data-testid="referral-prompt-copy-btn"
            className="btn-primary text-xs px-3 flex items-center gap-1.5 shrink-0"
          >
            {copied ? "Copied" : (<><Copy className="w-3.5 h-3.5" /> Copy</>)}
          </button>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={share}
            data-testid="referral-prompt-share-btn"
            className="btn-primary flex-1 flex items-center justify-center gap-1.5 text-sm"
          >
            <Send className="w-4 h-4" /> Share now
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="btn-ghost text-sm"
            data-testid="referral-prompt-later-btn"
            style={{ border: "1px solid var(--border-stone)", background: "#fff" }}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
